import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { useNavigate, useParams } from "react-router";

import { ApiError } from "@/api/client";
import {
  createTemplate,
  getTemplate,
  previewTemplate,
  sendTestTemplate,
  updateTemplate,
} from "@/api/messaging";
import { ConfirmModal } from "@/components/ConfirmModal";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { Field, Input, Select } from "@/components/form";
import { ErrorBox, MutationError, Spinner } from "@/components/Spinner";
import type {
  MessagingPreviewProfile,
  MessagingTemplate,
  MessagingTemplateCategory,
  MessagingTemplateInput,
} from "@/types/api";
import { Button, FormActionBar, Notice, Page, PageHeader } from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

const VARIABLES = ["given_name", "family_name", "email", "tenant_name", "tenant_id", "stage", "login_url"];

const PROFILES: { value: MessagingPreviewProfile; label: string }[] = [
  { value: "tenant_user", label: "Tenant user" },
  { value: "tenant_admin", label: "Tenant admin" },
  { value: "operator", label: "Operator" },
];

/** Two starting points offered on creation; the operator adapts them. */
const EXAMPLES: { label: string; input: MessagingTemplateInput }[] = [
  {
    label: "MFA reminder",
    input: {
      slug: "mfa-reminder",
      name: "MFA reminder",
      category: "service",
      subject: "Please enable two-factor authentication on {{tenant_name}}",
      heading: "Protect your account",
      bodyMarkdown:
        "Hi {{given_name}},\n\nYour {{tenant_name}} account does not have two-factor authentication yet. It takes a minute and protects your channels from a stolen password.\n\n1. Sign in: [{{login_url}}]({{login_url}})\n2. Open your profile settings\n3. Enable the authenticator app\n\nThank you,\nThe PulseStation team",
      replyTo: "",
    },
  },
  {
    label: "Product news",
    input: {
      slug: "product-news",
      name: "Product news",
      category: "news",
      subject: "What's new in PulseStation",
      heading: "What's new",
      bodyMarkdown:
        "Hi {{given_name}},\n\nHere is what changed on {{tenant_name}} this month:\n\n- **Feature one** — a short sentence on what it does for you.\n- **Feature two** — another one.\n\nSign in to try them: [{{login_url}}]({{login_url}})\n\nThe PulseStation team",
      replyTo: "",
    },
  },
];

const EMPTY: MessagingTemplateInput = {
  slug: "",
  name: "",
  category: "service",
  subject: "",
  heading: "",
  bodyMarkdown: "",
  replyTo: "",
};

/** The fields of a stored template that the form edits. */
function inputOf(t: MessagingTemplate): MessagingTemplateInput {
  return {
    slug: t.slug,
    name: t.name,
    category: t.category,
    subject: t.subject,
    heading: t.heading,
    bodyMarkdown: t.bodyMarkdown,
    replyTo: t.replyTo,
  };
}

export function MessagingTemplateEditPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const { slug } = useParams();
  const isNew = !slug;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<MessagingTemplateInput>(EMPTY);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [profile, setProfile] = useState<MessagingPreviewProfile>("tenant_user");
  // The stored template is put into the form ONCE, when it first arrives (and again when the address
  // names another template): a later refetch must not overwrite what the operator is typing.
  const [primed, setPrimed] = useState(false);
  // A form that creates a template is open from the first render; one that edits it when it is
  // primed. Dirty = what the form shows differs from what it was opened (or last saved) with, and the
  // way out of the page is held while it is.
  const { dirty, reset: markSaved } = useDirtyValues(form, isNew || primed);
  const guard = useUnsavedChanges(dirty);
  // "Start from" replaces what is typed: asked first when something is.
  const [example, setExample] = useState<MessagingTemplateInput | null>(null);

  const existing = useQuery({
    queryKey: ["messaging", "template", slug],
    queryFn: () => getTemplate(token, slug ?? ""),
    enabled: !!token && !isNew,
  });

  useEffect(() => {
    setPrimed(false);
  }, [slug]);
  useEffect(() => {
    if (!existing.data || primed) return;
    setForm(inputOf(existing.data));
    setUpdatedAt(existing.data.updatedAt);
    setPrimed(true);
  }, [existing.data, primed]);

  const save = useMutation({
    mutationFn: () =>
      isNew ? createTemplate(token, form) : updateTemplate(token, form.slug, form, updatedAt ?? 0),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["messaging", "templates"] });
      queryClient.invalidateQueries({ queryKey: ["messaging", "template", saved.slug] });
      setUpdatedAt(saved.updatedAt);
      if (isNew) {
        markSaved();
        guard.leave(`/messaging/templates/${encodeURIComponent(saved.slug)}/edit`, { replace: true });
      } else {
        // Stays on the page: the form now holds what the server holds (as the server wrote it).
        setForm(inputOf(saved));
        markSaved();
      }
    },
  });

  // The 412 notice's Reload: the server's copy replaces the form — what was typed on top of an older
  // version is what the notice says is being given up.
  async function reload() {
    const fresh = await existing.refetch();
    if (!fresh.data) return;
    setForm(inputOf(fresh.data));
    setUpdatedAt(fresh.data.updatedAt);
    markSaved();
    save.reset();
  }

  const preview = useMutation({
    mutationFn: () => previewTemplate(token, form.slug, profile),
  });

  const test = useMutation({
    mutationFn: () => sendTestTemplate(token, form.slug, profile),
  });

  const set = <K extends keyof MessagingTemplateInput>(key: K, value: MessagingTemplateInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // How a failed save says why: 412 means someone else saved a newer copy while this form was open
  // (optimistic lock); 409 on creation means the identifier is taken. Both are expected, recoverable
  // states — a specific message instead of the raw backend error.
  function saveFailure(error: unknown) {
    if (error instanceof ApiError && error.status === 412) {
      return (
        <Notice
          tone="warning"
          role="alert"
          actions={
            <Button variant="ghost" onClick={() => void reload()}>
              Reload
            </Button>
          }
        >
          This template was changed by someone else. Reload to see the latest version — what you typed
          on this form will be replaced.
        </Notice>
      );
    }
    if (isNew && error instanceof ApiError && error.status === 409) {
      return (
        <Notice tone="warning" role="alert">
          This identifier is already used by another template.
        </Notice>
      );
    }
    return undefined;
  }

  return (
    <Page>
      <PageHeader
        back={{ to: "/messaging/templates", label: "Templates" }}
        // The stored name: it does not change while the form is typed in. While it loads the title is
        // a bar, and if it cannot load the address still gives the identifier.
        title={isNew ? "New template" : existing.data ? existing.data.name : existing.isError ? slug : undefined}
      />

      {existing.isLoading && <Spinner />}
      {existing.isError && <ErrorBox error={existing.error} />}

      {(isNew || existing.data) && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <div className="card space-y-4">
              {isNew && (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-ink-muted">Start from:</span>
                  {EXAMPLES.map((ex) => (
                    <Button
                      key={ex.label}
                      variant="ghost"
                      onClick={() => (dirty ? setExample(ex.input) : setForm(ex.input))}
                    >
                      {ex.label}
                    </Button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Identifier">
                  <Input
                    value={form.slug}
                    disabled={!isNew}
                    placeholder="mfa-reminder"
                    onChange={(e) => set("slug", e.target.value)}
                    required
                  />
                </Field>
                <Field label="Name">
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} required />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <Select
                    value={form.category}
                    onChange={(e) => set("category", e.target.value as MessagingTemplateCategory)}
                    options={[
                      { value: "service", label: "Service (operation or security)", hint: "Never opted out" },
                      { value: "news", label: "News (product updates)" },
                    ]}
                  />
                </Field>
                <Field label="Reply-to (optional)">
                  <Input value={form.replyTo} onChange={(e) => set("replyTo", e.target.value)} placeholder="support@pulsestation.io" />
                </Field>
              </div>
              <Field label="Subject">
                <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} required />
              </Field>
              <Field label="Heading (optional, defaults to the subject)">
                <Input value={form.heading} onChange={(e) => set("heading", e.target.value)} />
              </Field>
              <div>
                <div className="mb-1 flex items-center justify-between text-sm text-ink-muted">
                  <span>Body (Markdown)</span>
                  <span className="text-xs text-ink-muted">
                    Variables: {VARIABLES.map((v) => `{{${v}}}`).join(" ")}
                  </span>
                </div>
                <MarkdownEditor value={form.bodyMarkdown} onChange={(v) => set("bodyMarkdown", v)} height={360} />
            </div>
            </div>

            <FormActionBar
              dirty={dirty}
              submitLabel={isNew ? "Create template" : "Save"}
              pending={save.isPending}
              onCancel={() => navigate("/messaging/templates")}
              error={<MutationError mutation={save} title="Could not save the template" view={saveFailure} />}
            />
          </form>

          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-ink">Email preview</h2>
              <div className="flex items-center gap-2">
                <Select
                  aria-label="Preview profile"
                  value={profile}
                  onChange={(e) => setProfile(e.target.value as MessagingPreviewProfile)}
                  options={PROFILES}
                />
                <Button variant="secondary" loading={preview.isPending} disabled={isNew} onClick={() => preview.mutate()}>
                  {preview.isPending ? "Rendering…" : "Preview"}
                </Button>
                <Button
                  variant="secondary"
                  loading={test.isPending} disabled={isNew}
                  onClick={() => test.mutate()}
                  title="Sends the preview to your own address"
                >
                  {test.isPending ? "Sending…" : "Send test to me"}
                </Button>
              </div>
            </div>
            <p className="text-sm text-ink-muted">
              {isNew
                ? "Save the template first to preview it."
                : "The preview and the test use the saved version of the template, not what is being typed."}
            </p>
            <MutationError mutation={preview} title="Could not render the preview" />
            <MutationError mutation={test} title="Could not send the test" />
            {test.data && (
              <Notice tone="success" role="status">
                Test sent to {test.data.to}. It will appear in Messaging › Emails as a test send.
              </Notice>
            )}
            {preview.data && (
              <>
                <div className="text-sm">
                  <span className="text-ink-muted">Subject:</span> <strong>{preview.data.subject}</strong>
                </div>
                <iframe
                  title="Email preview"
                  sandbox=""
                  srcDoc={preview.data.html}
                  className="h-[640px] w-full rounded border border-line bg-surface"
                />
              </>
            )}
          </div>
        </div>
      )}

      {example && (
        <ConfirmModal
          title="Replace what you typed?"
          confirmLabel="Replace"
          cancelLabel="Keep editing"
          onCancel={() => setExample(null)}
          onConfirm={() => {
            setForm(example);
            setExample(null);
          }}
        >
          <p>Starting from this example replaces every field of the form. What you typed so far will be lost.</p>
        </ConfirmModal>
      )}
    </Page>
  );
}
